import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { getEmailCaps, resendViaBrevo } from "@/lib/emailCaps";

export const OPEN_ISSUE = ["refused", "invalid_format", "brevo_failed", "brevo_unknown", "brevo_pending"];
// Issues of the latest send; legacy quotes only carry email_rejected
export const issuesOf = (q) => (q.send_issues?.length ? q.send_issues : q.email_rejected ? [{ to: q.email_rejected, state: "refused", legacy: true }] : []);
export const hasOpenIssue = (q) => issuesOf(q).some((i) => OPEN_ISSUE.includes(i.state));

const LOOK = {
  invalid_format: ["Adresse au format invalide", "bg-amber-100 text-amber-800 hover:bg-amber-200"],
  refused: ["Refusée par le service d'envoi", "bg-destructive/10 text-destructive hover:bg-destructive/20"],
  brevo_pending: ["Renvoi Brevo en cours", "bg-slate-100 text-slate-700 hover:bg-slate-200"],
  brevo_unknown: ["Renvoi Brevo : résultat inconnu", "bg-slate-100 text-slate-700 hover:bg-slate-200"],
  brevo_failed: ["Échec du renvoi Brevo", "bg-destructive/10 text-destructive hover:bg-destructive/20"],
  brevo_accepted: ["Renvoyé via Brevo (accepté)", "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"],
};

const IssueBadge = ({ quote, issue, idx, onSaved, onChanged, t, brevo }) => {
  const [open, setOpen] = useState(false);
  const isClient = issue.to === (quote.client?.email || "").trim();
  const [email, setEmail] = useState(issue.to);
  const [label, cls] = LOOK[issue.state] || LOOK.refused;
  const n = `${quote.quote_number}-${idx}`;
  const save = async () => {
    try {
      const { data } = await api.put(`/quotes/${quote.id}/client-email`, { email });
      toast.success(t("Adresse email du client corrigée"));
      setOpen(false);
      onSaved(quote.id, data.email);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resend = async () => { setOpen(false); if (await resendViaBrevo(quote.id, issue.to)) onChanged(); };
  const canFix = isClient && issue.state !== "brevo_accepted";
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setEmail(issue.to); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`send-issue-${n}`} data-state={issue.state}
          className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${cls}`}>
          {issue.state === "brevo_accepted" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {t(label)}{String(issue.message_id || "").startsWith("sim-") && ` (${t("simulé")})`}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid={`send-issue-popover-${n}`}>
        <div className="text-xs font-semibold">{t(label)}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 mb-2 break-all" data-testid={`send-issue-to-${n}`}>{issue.to}</div>
        {issue.state === "refused" && <p className="text-[11px] text-muted-foreground mb-2">{t("L'adresse n'est pas forcément incorrecte : le service d'envoi l'a refusée avant l'envoi.")}</p>}
        {canFix && <>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-8 text-sm" data-testid={`email-fix-input-${n}`} />
          <Button size="sm" className="w-full mt-2" onClick={save} disabled={!email.trim()} data-testid={`email-fix-save-${n}`}>{t("Corriger l'adresse")}</Button>
        </>}
        {brevo && issue.state === "refused" && !issue.legacy && (
          <Button size="sm" variant="outline" className="w-full mt-2 text-xs" onClick={resend} data-testid={`brevo-resend-${n}`}>
            <Send className="w-3 h-3 mr-1" /> {t("Réessayer via Brevo")}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const SendIssuesBadge = ({ quote, onSaved, onChanged, t }) => {
  const [brevo, setBrevo] = useState(false);
  useEffect(() => { getEmailCaps().then((c) => setBrevo(!!c.brevo)); }, []);
  return issuesOf(quote).map((i, k) => (
    <div key={`${i.to}-${k}`}><IssueBadge quote={quote} issue={i} idx={k} onSaved={onSaved} onChanged={onChanged} t={t} brevo={brevo} /></div>
  ));
};
